import { Fragment, type ReactElement } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@corpuscomplete/web-ui/components/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Skeleton } from "@corpuscomplete/web-ui/components/skeleton";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { Scope } from "@corpuscomplete/core/common/scope";
import { ScopeUI } from "@corpuscomplete/core-ui/common/scope";

type ScopeBreadcrumbCrumb =
  | { loading: true; label?: never; current?: never; renderLink?: never }
  | { label: string; current: true; renderLink?: never }
  | { label: string; current?: never; renderLink: ReactElement; loading?: never };

type ScopeBreadcrumbProps = {
  crumbs: ScopeBreadcrumbCrumb[];
  scope: Scope.Info | null;
} & (
  | { scopeBehavior: "switchable"; onScopeChange: (scopeId: string) => void }
  | { scopeBehavior: "static"; onScopeChange?: never }
);

export function ScopeBreadcrumb({
  crumbs,
  onScopeChange,
  scope,
  scopeBehavior,
}: ScopeBreadcrumbProps) {
  const { userId } = useAuth();
  const scopeId = scope ? Scope.id(scope) : null;
  const scopeOptions = ScopeUI.useOptions(userId);
  const selectedScopeLabel = ScopeUI.useLabel(userId, scopeId ?? userId);

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          {!scope ? (
            <LoadingCrumb className="w-20" />
          ) : scopeBehavior === "switchable" ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <BreadcrumbLink
                    render={
                      <button
                        type="button"
                        className="rounded-sm outline-none"
                        aria-label={`Change scope, current scope: ${selectedScopeLabel}`}
                      />
                    }
                  />
                }
              >
                {selectedScopeLabel}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" matchTriggerWidth={false}>
                <DropdownMenuRadioGroup value={Scope.id(scope)} onValueChange={onScopeChange}>
                  {scopeOptions.map((scope) => (
                    <DropdownMenuRadioItem key={scope.id} value={scope.id} closeOnClick>
                      <Text variant="xs">{scope.label}</Text>
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Text variant="xs">{selectedScopeLabel}</Text>
          )}
        </BreadcrumbItem>
        {crumbs.map((crumb, index) => (
          <Fragment key={"loading" in crumb ? `${index}:loading` : `${index}:${crumb.label}`}>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {"loading" in crumb ? (
                <LoadingCrumb className="w-16" />
              ) : crumb.current ? (
                <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink render={crumb.renderLink}>{crumb.label}</BreadcrumbLink>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function LoadingCrumb({ className }: { className?: string }) {
  return <Skeleton className={className ? `h-4 ${className}` : "h-4 w-16"} />;
}
